// UI 진입점(DOM 오버레이): HUD, 터치 조작, 화면(타이틀·결과·패배·일시정지)과 진단 표시를 묶는다.
// 게임 상태는 읽기만 하고 변경은 GameCommand와 InputSink로만 한다.
import type { GameCommand, GameView, InputSink } from '../shared/types';
import type { ScreenReserve } from './screenReserve';
import { designScale, el, preloadHud } from './dom';
import { installAutoFullscreen, installLandscapeKeeper, toggleFullscreen } from './fullscreen';
import { Hud } from './hud';
import { RpgUi } from './rpg';
import { RebirthUi } from './rebirth';
import { RegionCard } from './regionCard';
import { StageMapUi } from './stageMap';
import { TravelCard } from './travelCard';
import { installCameraGestures } from './cameraGestures';
import { loadPrefs, savePrefs } from './prefs';
import { Screens } from './screens';
import { isTouchDevice, TouchControls } from './touch';
import { AutoQuestUi } from './autoQuest';
import { InAppBanner } from './inAppBanner';
import { viewW, viewH } from '../shared/viewport';
import { LootFeed } from './lootFeed';
import { AvatarToast } from './avatarToast';
import { SpecialBand } from './specialBand';
import { JobCutscene } from './jobCutscene';
import { OfferBand } from '../iap/offers';
import { iapClient } from '../iap/client';
import { isAppChannel } from '../shared/appChannel';
import { FieldHud } from './fieldHud';
import { FieldMapUi } from './fieldMap';
import { ForgeFx } from './forgeFx';
import { RaidHud } from './raidHud';
import { HelpOverlay } from './helpOverlay';
import { DAILY_TOGGLE_EVENT, HELP_EVENT } from './uiEvents';
import { hotkeyStateOf, isTypingTarget, routeHotkey, routeMenu } from './hotkeys';
import { MENU_EVENT, menuBadges } from './menuBar';
import type { MenuId } from './hudMenuLayout';
import { AUTO_QUEST_EVENT } from './autoQuest';
import { RETURN_SCROLL_EVENT } from './potionHud';
import { makeRequestId } from './rpgModel';
import { installTooltips } from './tooltip';
import { uiSizeFactor } from './uiSize';
import { isRegionId, regionNoOf, type RegionId } from '../shared/levels';
import { Scene, combatScene } from '../shared/types';
import { keymap, windowKeyOf } from '../shared/keymap';
import { KeymapHint } from './keymapHint';

export interface UIHandle {
  update(frameDt: number): void;
}

export interface UIOptions {
  backend: string;
  /** HUD 예약 영역. HUD가 쓰고 렌더러의 피해 숫자가 읽는다(createRenderer에 넘긴 같은 객체) */
  reserve?: ScreenReserve;
}

export function createUI(root: HTMLElement, view: GameView, input: InputSink, send: (c: GameCommand) => void, opts: UIOptions): UIHandle {
  const params = new URLSearchParams(location.search);
  const prefs = loadPrefs();
  const touchOn = isTouchDevice(params);
  // 자동 물약 문턱(D2c, UI 설정에 저장): 시작할 때 게임에 한 번 알린다
  if (prefs.autoPotion > 0) send({ type: 'setAutoPotion', pct: prefs.autoPotion });
  // HUD 원화(틀·아이콘·초상)를 첫 화면 전에 받아 둔다
  preloadHud();

  // 키보드 기기는 HUD가 하단 스킬 바를 만든다(슬롯 마우스 클릭도 터치와 같은 InputSink로 보낸다)
  const hud = new Hud(root, view, send, touchOn, input, opts.reserve ?? null);
  const touch = new TouchControls(root, view, input, prefs.touchScale, send, { pos: prefs.touchPos, opacity: prefs.touchOpacity });
  const rpg = new RpgUi(root, view, send, touchOn, input);
  // 윤회의 문(레인 R): 안내·확인 화면·새 모드 소개 카드
  const rebirth = new RebirthUi(root, view, send, input, touchOn);
  const stageMap = new StageMapUi(root, view, send);
  const regionCard = new RegionCard(root, view, send, (r) => stageMap.prepare(r));
  // 마을 이동 연출(D3a): 화면 가장자리 지역 제목 카드 + 짧은 페이드(플레이 영역 가림 없음)
  const travelCard = new TravelCard(root, view);
  installCameraGestures(view);
  const screens = new Screens(root, view, send, prefs, (s) => touch.setScale(s), touchOn, {
    onOpacity: (o) => touch.setOpacity(o),
    onEdit: () => touch.openEditor((pos) => { prefs.touchPos = pos; savePrefs(prefs); }),
  });
  // 자동 퀘스트 진행(퀘스트 카드·던전 목표 패널의 「자동」, Q 키)
  const autoQuest = new AutoQuestUi(root, view, input, send, touchOn);
  // 인앱 브라우저 안내 띠(위쪽 가장자리, 타이틀·마을에서만). 실제 브라우저에서는 DOM을 만들지 않는다
  const inApp = new InAppBanner(root, view);
  // 드롭 줍기 알림: 화면 왼쪽 가장자리 띠(플레이 영역 위 팝업 없음)
  const lootFeed = new LootFeed(root, view);
  const avatarToast = new AvatarToast(root, view);
  // 스페셜 몬스터 알림 띠: 화면 위쪽 가장자리, HUD 사이 빈 띠(플레이 영역 가림 없음)
  const specialBand = new SpecialBand(root, view);
  // ARPG 필드(레인 F): 구역명·조각·이벤트 띠·미니맵·균열 게이지, 월드 맵
  const fieldHud = new FieldHud(root, view, send, touchOn ? touch : null);
  const fieldMap = new FieldMapUi(root, view, send);
  // 대장간 강화 순간 연출(c017): 강화 카드 위 작은 캔버스만(마을 대장간 창 안)
  const forgeFx = new ForgeFx(root, view);
  // 전직 연출(C10-5): Ev.JobChange 성공 때 레터박스·빛기둥·문양 컷(탭하면 건너뜀)
  const jobCut = new JobCutscene(root, view);
  // 결제 제안 띠(레벨 10·20·30·40, 전직, 2연속 패배 뒤 마을에서 위쪽 가장자리 한 줄)
  const offerBand = new OfferBand(root, view);
  // 앱(TWA)이면 시작할 때 결제 클라이언트를 한 번 열어 미완료 Play 구매를 복원한다(상점을 안 열어도 3일 자동 환불 전에 젬이 들어가게)
  if (isAppChannel()) iapClient();
  // 레이드 파티 창·페이즈 줄(RAID-PRD 9절): 레이드 판에서만 보인다
  const raidHud = new RaidHud(root, send, touchOn);
  // 마우스 툴팁(PC): 버튼·아이템에 마우스를 올리면 설명 상자
  installTooltips(root);
  // 단축키 안내(F1·?): 열려 있는 동안 게임을 일시정지하고, 닫으면 우리가 건 일시정지만 푼다
  let pausedByHelp = false;
  const help = new HelpOverlay(root, () => { if (pausedByHelp) { pausedByHelp = false; send({ type: 'setPaused', paused: false }); } });
  const toggleHelp = (): void => {
    if (help.isOpen) { help.close(); return; }
    const f = view.flow;
    if ((f.scene === Scene.Village || combatScene(f.scene)) && !f.paused) { pausedByHelp = true; send({ type: 'setPaused', paused: true }); }
    help.open();
  };
  window.addEventListener(HELP_EVENT, toggleHelp);
  /** M 키 지도: 이 마을 문 중 열린 가장 먼 지역의 스테이지 지도 */
  const mapRegion = (): RegionId => {
    let best: RegionId = 'r1';
    for (const e of view.map.exits) if (isRegionId(e.to) && view.progress.regionUnlocked(e.to) && regionNoOf(e.to) >= regionNoOf(best)) best = e.to;
    return best;
  };
  // PC 스킬 바 귀환서 칸(두 번 눌러 사용): 일시정지 창 「귀환서 사용」과 같은 명령
  window.addEventListener(RETURN_SCROLL_EVENT, () => { if (combatScene(view.flow.scene) && !view.flow.paused) send({ type: 'useReturnScroll', requestId: makeRequestId() }); });
  /** 메뉴 줄 「상점」을 다른 창이 열린 채 눌렀을 때: 창이 닫힌 뒤 잡화상으로 출발 */
  let pendingShop = false;
  // 오른쪽 위 아이콘 메뉴 줄(menuBar.ts, 2026-10-27): 누르면 그 창이 바로 열린다. 단축키와 같은 명령·같은 창 열기 함수를 쓴다
  window.addEventListener(MENU_EVENT, (e) => {
    const r = routeMenu((e as CustomEvent<MenuId>).detail, hotkeyStateOf(view, help.isOpen));
    if (!r) return;
    if (r.close) send({ type: 'closeMenu' });
    switch (r.open) {
      case 'pause': send({ type: 'setPaused', paused: true }); break;
      case 'bag': rpg.openTabNext('equip'); send({ type: 'openInventory' }); break;
      case 'skill': rpg.openTabNext('skills'); send({ type: 'openInventory' }); break;
      case 'stat': rpg.openTabNext('status'); send({ type: 'openInventory' }); break;
      case 'set': rpg.openTabNext('sets'); send({ type: 'openInventory' }); break;
      case 'quest': send({ type: 'openQuest' }); break;
      case 'map': stageMap.prepare(mapRegion()); send({ type: 'openStageMap' }); break;
      case 'daily': window.dispatchEvent(new CustomEvent(DAILY_TOGGLE_EVENT)); break;
      // 상점: 잡화상까지 자동으로 걸어가 말을 건다(마을 목표 자동 이동과 같은 길찾기 — 순간이동 없음)
      // 다른 창이 열려 있으면 닫힌 다음 프레임에 출발한다(자동 이동은 창이 열린 동안 시작하지 않는다)
      case 'shop': pendingShop = true; break;
      default: break;
    }
  });
  window.addEventListener('keydown', (e) => {
    const r = routeHotkey(e, hotkeyStateOf(view, help.isOpen), isTypingTarget(e.target), keymap.preset);
    if (!r) return;
    e.preventDefault();
    if (r.type === 'help') toggleHelp();
    else if (r.type === 'close') {
      // 가방이 열린 채 다른 창 키(K 스킬·C 스탯·N)를 누르면 그 탭으로 바꾸고, 이미 그 탭이면 닫는다
      const w = windowKeyOf(e.code, keymap.preset);
      const tab = w === 'skills' ? 'skills' : w === 'stats' ? 'status' : null;
      if (!(tab && view.village.inventoryOpen && rpg.switchTab(tab))) send({ type: 'closeMenu' });
    }
    else if (r.action === 'quest') send({ type: 'openQuest' });
    else if (r.action === 'stats') { rpg.openTabNext('status'); send({ type: 'openInventory' }); }
    else if (r.action === 'skills') { rpg.openTabNext('skills'); send({ type: 'openInventory' }); }
    else { stageMap.prepare(mapRegion()); send({ type: 'openStageMap' }); }
  });
  // 기존 사용자 키 배치 안내(마을 첫 진입 때 위쪽 가장자리 띠 한 번)
  const keyHint = new KeymapHint(root, view, touchOn);
  // 전체 화면: 설정이 켜져 있으면 다음 탭에서 자동으로 들어가고, PC는 F 키로 전환한다
  installAutoFullscreen(touchOn);
  // 터치 기기는 세션 내내 가로(c014): 전체 화면 재진입·앱 복귀 때 가로 고정을 다시 건다
  installLandscapeKeeper(touchOn);
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyF' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
    toggleFullscreen();
  });

  // 렌더 백엔드·FPS·단계 진단은 명시적인 ?qa 주소에서만 만든다(일반 플레이 화면에는 DOM 자체가 없다)
  const diag = params.has('qa') ? el('div', 'position:absolute;left:8px;bottom:6px;color:#fff;font:11px monospace;opacity:.55;', root) : null;
  let fpsAcc = 0, fpsFrames = 0, fps = 0;
  let winOpen = false;
  return {
    update(frameDt: number) {
      const u = designScale(viewW(), viewH()) * uiSizeFactor(viewW());
      hud.update(u);
      hud.anchorAwaken(touch.gaugeAnchor, u);
      screens.update(u);
      rpg.update(u);
      rebirth.update();
      regionCard.update(u);
      stageMap.update();
      travelCard.update();
      autoQuest.update(u);
      inApp.update();
      keyHint.update();
      lootFeed.update(u, performance.now());
      avatarToast.update(view);
      specialBand.update(u, performance.now());
      fieldHud.update(u, performance.now());
      hud.suppressLinkHint(fieldHud.choiceVisible);
      fieldMap.update();
      forgeFx.update();
      jobCut.update(u, performance.now());
      offerBand.update();
      raidHud.update(view, u);
      // 터치 조이스틱 영역은 퀘스트 카드·골드 줄 사각형을 피하므로, 그것들이 이번 프레임에 정해진 뒤에 맞춘다(한 프레임 늦어 카드 밑에 깔리는 것을 막는다)
      touch.update(touchOn);
      // 창이 열려 있으면 커뮤니티 말풍선을 숨긴다(오른쪽 패널·일일 게시판을 가리던 문제, 2026-10-27 대표님 화면)
      const vv = view.village;
      const win = vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen || view.flow.paused || menuBadges.dailyOpen || help.isOpen;
      if (win !== winOpen) { winOpen = win; document.body.classList.toggle('dh-win-open', win); }
      if (pendingShop && !win) { pendingShop = false; if (view.flow.scene === Scene.Village) window.dispatchEvent(new CustomEvent(AUTO_QUEST_EVENT, { detail: { kind: 'npc', role: 'shop' } })); }
      if (!diag) return;
      fpsAcc += frameDt;
      fpsFrames++;
      if (fpsAcc >= 0.5) {
        fps = Math.round(fpsFrames / fpsAcc);
        fpsAcc = 0;
        fpsFrames = 0;
        diag.textContent = opts.backend + ' ' + fps + 'fps ' + view.flow.stageLabel;
      }
    },
  };
}
